// @ts-nocheck
// lib/api.ts
// Centralized API utility functions for consistent backend communication

import { getApiBaseUrl } from '../config';
import { ACTIVITY_LABEL, ActivityType } from './activityTypes';

const API_URL = getApiBaseUrl();
export { API_URL };

let csrfToken: string | null = null;

const DEFAULT_TIMEOUT = 10000;

export interface ApiError extends Error {
  status?: number;
  data?: {
    message?: string;
    [key: string]: any;
  };
}

const fetchCsrfToken = async () => {
  if (csrfToken) return csrfToken;
  try {
    const res = await fetch(`${API_URL}/auth/csrf-token`, {
      credentials: 'include',
    });
    const data = await res.json();
    csrfToken = data.csrfToken;
  } catch (e) {
    console.error('Failed to fetch CSRF token', e);
  }
  return csrfToken;
};

// Main API request function
export const apiFetch = async (
  endpoint: string,
  options: any = {},
) => {
    const { timeout = DEFAULT_TIMEOUT, ...rest } = options;
    const url = `${API_URL}${endpoint}`;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);

    const config: any = {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // Important for httpOnly cookies
      signal: controller.signal,
      ...rest,
    };

  // Handle request body
  if (config.body && typeof config.body === 'object' && !(config.body instanceof FormData)) {
    config.body = JSON.stringify(config.body);
  }

  // Attach CSRF token for state-changing requests
  if (config.method && config.method.toUpperCase() !== 'GET') {
    const token = await fetchCsrfToken();
    if (token) {
      config.headers['X-CSRF-Token'] = token;
    }
  }

  try {
      const response = await fetch(url, config);
    
    // Handle different response types
    let data;
    const contentType = response.headers.get('content-type');
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    // Handle error responses
    if (!response.ok) {
      const error: ApiError = new Error(data.message || `HTTP ${response.status}`);
      error.status = response.status;
      error.data = data;
      throw error;
    }

      return { data, response };
    
  } catch (error) {
    // Network or parsing errors
      console.error('API Request Error:', error);
      throw error;
    } finally {
      clearTimeout(timer);
    }
  };

// Specific API methods
export const api = {
  // Authentication
  auth: {
    login: (credentials) => apiFetch('/auth/login', {
      method: 'POST',
      body: credentials,
    }),

    register: (userData) => apiFetch('/auth/register', {
      method: 'POST',
      body: userData,
    }),
    
    logout: () => apiFetch('/auth/logout', {
      method: 'POST',
    }),
    
    me: () => apiFetch('/auth/me'),
    
    refresh: () => apiFetch('/auth/refresh', {
      method: 'POST',
    }),

    // Password Reset endpoints
    requestPasswordReset: (data) => apiFetch('/auth/forgot-password', {
      method: 'POST',
      body: data,
    }),

    validateResetToken: (token) => apiFetch('/auth/reset-password/validate', {
      method: 'POST',
      body: { token },
    }),

    resetPassword: (data) => apiFetch('/auth/reset-password', {
      method: 'POST',
      body: data,
    }),
  },

  // Users
  users: {
    getAll: (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return apiFetch(`/users${queryString ? `?${queryString}` : ''}`);
    },

    getById: (id) => apiFetch(`/users/${id}`),

    update: (id, userData) => apiFetch(`/users/${id}`, {
      method: 'PUT',
      body: userData,
    }),
    
    changePassword: (id, passwordData) => apiFetch(`/users/${id}/password`, {
      method: 'PUT',
      body: passwordData,
    }),
    
    updateStatus: (id, status) => apiFetch(`/users/${id}/status`, {
      method: 'PUT',
      body: { isActive: status },
    }),
    
    delete: (id) => apiFetch(`/users/${id}`, {
      method: 'DELETE',
    }),
  },

  // Wells
  wells: {
    getAll: (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return apiFetch(`/wells${queryString ? `?${queryString}` : ''}`);
    },

    getById: (id) => apiFetch(`/wells/${id}`),

    create: (wellData) => apiFetch('/wells', {
      method: 'POST',
      body: wellData,
    }),
    
    update: (id, wellData) => apiFetch(`/wells/${id}`, {
      method: 'PUT',
      body: wellData,
    }),
    
    delete: (id) => apiFetch(`/wells/${id}`, {
      method: 'DELETE',
    }),
    
    getStats: () => apiFetch('/wells/stats/overview'),

    getProduction: (id, days = 30) => apiFetch(`/wells/${id}/production?days=${days}`),
  },

  // Companies
  companies: {
    getAll: (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return apiFetch(`/companies${queryString ? `?${queryString}` : ''}`);
    },

    getById: (id) => apiFetch(`/companies/${id}`),

    create: (data) => apiFetch('/companies', {
      method: 'POST',
      body: data,
    }),

    update: (id, data) => apiFetch(`/companies/${id}`, {
      method: 'PUT',
      body: data,
    }),

    delete: (id) => apiFetch(`/companies/${id}`, {
      method: 'DELETE',
    }),
  },

  // Activities
  activities: {
    getAll: (params = {}) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (key === 'exclude') {
          const excludes = Array.isArray(value) ? value : [value];
          excludes.forEach((v) => {
            if ((Object.keys(ACTIVITY_LABEL) as ActivityType[]).includes(v as ActivityType)) {
              searchParams.append('exclude', v as string);
            }
          });
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      return apiFetch(`/activities${queryString ? `?${queryString}` : ''}`);
    },

    getById: (id) => apiFetch(`/activities/${id}`),

    create: (activityData) => apiFetch('/activities', {
      method: 'POST',
      body: activityData,
    }),
    
    update: (id, activityData) => apiFetch(`/activities/${id}`, {
      method: 'PUT',
      body: activityData,
    }),

    delete: (id) => apiFetch(`/activities/${id}`, {
      method: 'DELETE',
    }),

    getStats: (range = '30d') => apiFetch(`/activities/summary/stats?range=${range}`),

    getTimeline: (wellId, days = 30) => apiFetch(`/activities/timeline/${wellId}?days=${days}`),
  },

  activityDefinitions: {
    list: () => apiFetch('/activity-definitions'),
    create: (data) => apiFetch('/activity-definitions', { method: 'POST', body: data }),
    update: (id, data) => apiFetch(`/activity-definitions/${id}`, { method: 'PUT', body: data }),
    delete: (id) => apiFetch(`/activity-definitions/${id}`, { method: 'DELETE' })
  },

  // Files
  files: {
    getAll: (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return apiFetch(`/files${queryString ? `?${queryString}` : ''}`);
    },

    getById: (id) => apiFetch(`/files/${id}`),

    upload: (formData) => apiFetch('/files', {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set content-type for FormData
    }),
    
    download: (id) => apiFetch(`/files/${id}/download`),

    update: (id, fileData) => apiFetch(`/files/${id}`, {
      method: 'PUT',
      body: fileData,
    }),
    
    delete: (id) => apiFetch(`/files/${id}`, {
      method: 'DELETE',
    }),
    
    bulkDelete: (fileIds) => apiFetch('/files/bulk-delete', {
      method: 'POST',
      body: { fileIds },
    }),
    
    getStats: () => apiFetch('/files/stats/overview'),
  },

  // Notifications
  notifications: {
    getAll: (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return apiFetch(`/notifications${queryString ? `?${queryString}` : ''}`);
    },

    getUnreadCount: () => apiFetch('/notifications/unread/count'),

    create: (notificationData) => apiFetch('/notifications', {
      method: 'POST',
      body: notificationData,
    }),
    
    markAsRead: (id) => apiFetch(`/notifications/${id}/read`, {
      method: 'PUT',
    }),
    
    markAllAsRead: () => apiFetch('/notifications/read-all', {
      method: 'PUT',
    }),
    
    delete: (id) => apiFetch(`/notifications/${id}`, {
      method: 'DELETE',
    }),
    
    getAnalytics: (days = 30) => apiFetch(`/notifications/analytics?days=${days}`),

    getTemplates: () => apiFetch('/notifications/templates'),
  },

  // Search
  search: {
    global: (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return apiFetch(`/searches?${queryString}`);
    },

    getSuggestions: (query) => apiFetch(`/searches/suggestions?q=${encodeURIComponent(query)}`),

    getSaved: () => apiFetch('/searches/saved'),

    saveSearch: (searchData) => apiFetch('/searches/saved', {
      method: 'POST',
      body: searchData,
    }),
    
    deleteSaved: (id) => apiFetch(`/searches/saved/${id}`, {
      method: 'DELETE',
    }),
    
    getRecent: (limit = 10) => apiFetch(`/searches/recent?limit=${limit}`),

    getPopular: (days = 30, limit = 20) => apiFetch(`/searches/popular?days=${days}&limit=${limit}`),

    getAnalytics: (days = 30) => apiFetch(`/searches/analytics?days=${days}`),
  },

  // Admin
  admin: {
    getDashboard: () => apiFetch('/admin/dashboard'),

    getUserAnalytics: (range = '30d') => apiFetch(`/admin/users/analytics?range=${range}`),

    getSystemLogs: (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      return apiFetch(`/admin/system/logs${queryString ? `?${queryString}` : ''}`);
    },

    getSystemConfig: () => apiFetch('/admin/system/config'),

    updateSystemConfig: (config) => apiFetch('/admin/system/config', {
      method: 'PUT',
      body: config,
    }),
    
    bulkUserAction: (action, userIds) => apiFetch('/admin/users/bulk-action', {
      method: 'POST',
      body: { action, userIds },
    }),
    
    exportReports: (type, format = 'json') => apiFetch(`/admin/reports/export?type=${type}&format=${format}`),

    triggerBackup: () => apiFetch('/admin/system/backup', {
      method: 'POST',
    }),

    apiKeys: {
      list: () => apiFetch('/admin/api-keys'),
      routes: () => apiFetch('/admin/api-keys/routes'),
      create: (data: {
        name: string;
        permissions: string[];
        userId: string;
        expiresAt?: string;
      }) => apiFetch('/admin/api-keys', { method: 'POST', body: data }),
      update: (id: string, data: any) =>
        apiFetch(`/admin/api-keys/${id}`, { method: 'PUT', body: data }),
      remove: (id: string) => apiFetch(`/admin/api-keys/${id}`, { method: 'DELETE' }),
      regenerate: (id: string) =>
        apiFetch(`/admin/api-keys/${id}/regenerate`, { method: 'POST' }),
    },
  },
};

// Error handling utilities
export const handleApiError = (error: unknown) => {
  const err = error as ApiError;
  if (err.status === 401) {
    // Unauthorized - redirect to login
    localStorage.removeItem('user');
    
    // Check if we're in a browser environment before accessing window
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
    return 'Your session has expired. Please log in again.';
  }
  
  if (err.status === 403) {
    // Forbidden - show access denied
    return 'Access denied. You do not have permission to perform this action.';
  }
  
  if (err.status === 404) {
    // Not found
    return 'The requested resource was not found.';
  }

  if (err.status === 429) {
    // Too many requests
    return 'Too many requests. Please try again later.';
  }

  if (err.status === 422) {
    // Validation error
    return err.data?.message || 'Please check your input and try again.';
  }

  if (err.status === 429) {
    const retryAfter = err.data?.retryAfter;
    if (retryAfter) {
      const seconds = Math.ceil(Number(retryAfter) / 1000);
      return `${err.data?.message || 'Too many requests.'} Please try again in ${seconds} seconds.`;
    }
    return err.data?.message || 'Too many requests. Please try again later.';
  }

  if (err.status >= 500) {
    // Server error
    return 'Server error. Please try again later.';
  }

  // Return the error message from the API or a default message
  return err.data?.message || err.message || 'An unexpected error occurred.';
};

// Authentication check utility
export const isAuthenticated = () => {
  const user = localStorage.getItem('user');
  if (!user) return false;
  
  try {
    const parsedUser = JSON.parse(user);
    return parsedUser && parsedUser.id;
  } catch (error) {
    console.error('Error parsing user data:', error);
    localStorage.removeItem('user');
    return false;
  }
};

// Get current user utility
export const getCurrentUser = () => {
  const user = localStorage.getItem('user');
  if (!user) return null;

  try {
    return JSON.parse(user);
  } catch (error) {
    console.error('Error parsing user data:', error);
    localStorage.removeItem('user');
    return null;
  }
};

// Get activity feed preferences for current user
export const getActivityFeedPreferences = (): ActivityType[] => {
  const user = getCurrentUser();
  const types: string[] = user?.preferences?.activityFeed || [];
  const valid = types.filter((t): t is ActivityType =>
    (Object.keys(ACTIVITY_LABEL) as ActivityType[]).includes(t as ActivityType)
  );
  if (user && user.preferences && types.length !== valid.length) {
    user.preferences.activityFeed = valid;
    try {
      localStorage.setItem('user', JSON.stringify(user));
    } catch (err) {
      console.error('Error updating activityFeed preferences:', err);
    }
  }
  return valid;
};

// Update current user utility
export const updateCurrentUser = (userData) => {
  try {
    if (userData?.preferences?.activityFeed) {
      userData.preferences.activityFeed = userData.preferences.activityFeed.filter((t: string): t is ActivityType =>
        (Object.keys(ACTIVITY_LABEL) as ActivityType[]).includes(t as ActivityType)
      );
    }
    localStorage.setItem('user', JSON.stringify(userData));
    return userData;
  } catch (error) {
    console.error('Error updating user data:', error);
    return null;
  }
};

// Clear authentication data
export const clearAuth = () => {
  localStorage.removeItem('user');
};

// API response interceptor for automatic token refresh
export const setupApiInterceptors = () => {
  // This could be used to set up automatic token refresh
  // Implementation depends on your authentication strategy
};

export default api;